import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";

var _dec, _class, _class2;

import { model, Model, modelAction, prop } from "mobx-keystone";
import { DirectionHelper } from "./DirectionHelper";
import { computed } from "mobx";
import { getAllNumberEnumValues } from "../helper/generalHelpers";
import { Size3DModel } from "./Size3DModel";
import { TileLayerType } from "./TileLayerType";
import { adjustTileOffsetXY, adjustTileOffsetZ, adjustTileSizeXY, adjustTileSizeZ } from "../data/mapElementSorting";
import { TranslatedString } from "../game/TranslatedString";
export let TileVisibility;

(function (TileVisibility) {
  TileVisibility[TileVisibility["ShowAlways"] = 0] = "ShowAlways";
  TileVisibility[TileVisibility["ComplexOnly"] = 1] = "ComplexOnly";
  TileVisibility[TileVisibility["ProductionOnly"] = 2] = "ProductionOnly";
})(TileVisibility || (TileVisibility = {}));

export const tileVisibilities = getAllNumberEnumValues(TileVisibility);
export let TileAssetModel = (_dec = model("resources/TileAssetModel"), _dec(_class = (_class2 = class TileAssetModel extends Model({
  id: prop("").withSetter(),
  localizedName: prop(() => new TranslatedString({})),
  layerType: prop(TileLayerType.Ground).withSetter(),
  blockedTiles: prop(() => [[[]]]).withSetter(),
  planeTransit: prop().withSetter(),
  imageAssets: prop(() => new Array(4)),
  defaultGroundHeight: prop(null).withSetter(),
  tags: prop(() => []).withSetter(),
  visibilityInEditor: prop(() => TileVisibility.ShowAlways).withSetter(),
  offsetX: prop(0),
  offsetY: prop(0),
  internalOffsetZ: prop(0),
  size: prop(() => new Size3DModel({})),
  conflictResolutionOrigin: prop(0.5).withSetter()
}) {
  get tilesX() {
    return Math.max(1, Math.ceil(this.offsetX + this.size.x));
  }

  get tilesY() {
    return Math.max(1, Math.ceil(this.offsetY + this.size.y));
  }

  get offsetZComputed() {
    var _this$planeTransit;

    return this.layerType === TileLayerType.Decoration || (_this$planeTransit = this.planeTransit) !== null && _this$planeTransit !== void 0 && _this$planeTransit.isInitialized() ? 0 : -this.size.z;
  }

  setImageProperties(imageProperties, usage) {
    while (this.imageAssets.length <= usage) {
      this.imageAssets.push(null);
    }

    this.imageAssets[usage] = imageProperties;
  }

  imageProperties(usageIndex) {
    if (this.imageAssets.length <= usageIndex) {
      return null;
    }

    return this.imageAssets[usageIndex];
  }

  resizeBlockedTilesIfNecessary() {
    const {
      tilesX,
      tilesY
    } = this; // Add missing columns

    while (this.blockedTiles.length < tilesX) {
      this.blockedTiles.push([]);
    } // Remove overflowing columns


    this.blockedTiles.length = tilesX;

    for (let x = 0; x < tilesX; x++) {
      const column = this.blockedTiles[x]; // Add missing tiles in rows

      while (column.length < tilesY) {
        column.push([]);
      } // Remove overflowing tiles in rows


      column.length = tilesY;
    }
  }

  toggleBlockedAtOffset(offsetX, offsetY, direction) {
    if (!this.hasTileOffset(offsetX, offsetY)) throw new Error(`Tile offset outside of range: ${offsetX}|${offsetY} (tile extents is ${this.tilesX}|${this.tilesY})`);
    const blocked = this.blockedTiles[offsetX][offsetY];

    if (blocked.some(d => d === direction)) {
      blocked.splice(blocked.indexOf(direction), 1);
    } else {
      blocked.push(direction);
    }
  }

  isBlockedAtOffset(direction, offsetX, offsetY) {
    if (!this.hasTileOffset(offsetX, offsetY)) return false;
    const directionComponents = DirectionHelper.getComponents(direction);

    if (directionComponents) {
      return this.isBlockedAtOffset(directionComponents[0], offsetX, offsetY) || this.isBlockedAtOffset(directionComponents[1], offsetX, offsetY);
    }

    const blocked = this.blockedTiles[offsetX][offsetY];
    return blocked.some(d => d === direction);
  }

  hasTileOffset(offsetX, offsetY) {
    return offsetX >= 0 && offsetX < this.tilesX && offsetY >= 0 && offsetY < this.tilesY;
  }

  resetOffset() {
    this.offsetX = 0;
    this.offsetY = 0;
    this.internalOffsetZ = 0;
  }

  get isHeightFlexible() {
    var _this$planeTransit2;

    return this.layerType !== TileLayerType.Ground || ((_this$planeTransit2 = this.planeTransit) === null || _this$planeTransit2 === void 0 ? void 0 : _this$planeTransit2.isInitialized()) || !Number.isFinite(this.defaultGroundHeight);
  }

  isMadeForPlane(plane) {
    return this.isHeightFlexible || this.defaultGroundHeight === plane;
  }

  get isGround() {
    return this.layerType === TileLayerType.Ground;
  }

  setOffsetX(value) {
    this.offsetX = adjustTileOffsetXY(value);
    this.resizeBlockedTilesIfNecessary();
  }

  setOffsetY(value) {
    this.offsetY = adjustTileOffsetXY(value);
    this.resizeBlockedTilesIfNecessary();
  }

  setInternalOffsetZ(value) {
    this.internalOffsetZ = adjustTileOffsetZ(value);
  }

  setSizeX(value) {
    this.size.x = adjustTileSizeXY(value, this.isGround);
    this.resizeBlockedTilesIfNecessary();
  }

  setSizeY(value) {
    this.size.y = adjustTileSizeXY(value, this.isGround);
    this.resizeBlockedTilesIfNecessary();
  }

  setSizeZ(value) {
    this.size.z = adjustTileSizeZ(value);
  }

  translatableEntityData() {
    return {
      entity: this,
      label: "Tile",
      translateableStrings: [{
        label: "Name",
        translatedString: this.localizedName
      }]
    };
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "tilesX", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "tilesX"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "tilesY", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "tilesY"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "offsetZComputed", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "offsetZComputed"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setImageProperties", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "setImageProperties"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "resizeBlockedTilesIfNecessary", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "resizeBlockedTilesIfNecessary"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleBlockedAtOffset", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleBlockedAtOffset"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "resetOffset", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "resetOffset"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "isHeightFlexible", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "isHeightFlexible"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setOffsetX", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "setOffsetX"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setOffsetY", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "setOffsetY"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setInternalOffsetZ", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "setInternalOffsetZ"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setSizeX", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "setSizeX"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setSizeY", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "setSizeY"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setSizeZ", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "setSizeZ"), _class2.prototype)), _class2)) || _class);
export let TileImageUsage;

(function (TileImageUsage) {
  TileImageUsage[TileImageUsage["Background"] = 0] = "Background";
  TileImageUsage[TileImageUsage["WaterMask"] = 1] = "WaterMask";
  TileImageUsage[TileImageUsage["Foreground"] = 2] = "Foreground";
  TileImageUsage[TileImageUsage["WaterMaskForeground"] = 3] = "WaterMaskForeground";
})(TileImageUsage || (TileImageUsage = {}));

export const allTileImageUsages = getAllNumberEnumValues(TileImageUsage);
export const availableImageUsagesForLayerType = new Map();
availableImageUsagesForLayerType.set(TileLayerType.Ground, allTileImageUsages);
availableImageUsagesForLayerType.set(TileLayerType.Decoration, [TileImageUsage.Background]);
export function tileImageIdentificationToKey(id, tileImageUsage, version) {
  return `${id}/${tileImageUsage}/${version}`;
}
export function keyToTileImageIdentification(frameId) {
  const versionStart = frameId.lastIndexOf("/");
  const tileImageUsageStart = frameId.lastIndexOf("/", versionStart - 1);
  const id = frameId.slice(0, tileImageUsageStart);
  const tileImageUsage = +frameId.slice(tileImageUsageStart + 1, versionStart);
  const version = frameId.slice(versionStart + 1);
  return {
    id,
    tileImageUsage,
    version
  };
}
export const tileAssetFloorTag = "Boden";
export const mainCategoryTags = [tileAssetFloorTag];